import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter as UIDialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import {
  X,
  Users,
  TrendingDown,
  Calendar,
  Shield,
  Vote,
  BarChart3,
  Building2,
  Database,
  Network,
  Mail,
  CheckCircle,
  DollarSign,
  ShoppingBag,
  MessageSquare,
  Lock,
  HandCoins,
  Landmark,
  CreditCard,
  ThumbsUp,
  Clock,
  Car,
} from 'lucide-react';
import type { VideoCategory, VideoLibraryItem, VideoBadge } from '../types/video';

type VideoLibrarySectionProps = {
  videos: VideoLibraryItem[];
  initialCategory?: Exclude<VideoCategory, 'all'> | 'all';
  cityName?: string; // used in CTA copy
  onPrimaryCTA?: () => void; // optional handler for primary CTA in dialog
};

export function VideoLibrarySection({
  videos,
  initialCategory = 'all',
  cityName,
  onPrimaryCTA,
}: VideoLibrarySectionProps) {
  const [category, setCategory] = React.useState<VideoCategory>(initialCategory);
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState<VideoLibraryItem | null>(null);
  const [autoPlayWithSound, setAutoPlayWithSound] = React.useState(false);

  // Remember last selected category per-city if provided
  const storageKey = React.useMemo(() => `videoCategory:${cityName ?? 'default'}`, [cityName]);
  React.useEffect(() => {
    const stored = window.localStorage.getItem(storageKey);
    if (
      stored === 'all' ||
      stored === 'governance' ||
      stored === 'economic' ||
      stored === 'community'
    ) {
      setCategory(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const handleOpen = (video: VideoLibraryItem, withSound = true) => {
    setActive(video);
    setAutoPlayWithSound(!!withSound);
    setOpen(true);
  };

  const defaultCTA = () => {
    const el = document.getElementById('interest-form');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const categories: { key: VideoCategory; label: string; emoji: string }[] = [
    { key: 'all', label: 'All', emoji: '🎬' },
    { key: 'governance', label: 'Governance', emoji: '🗳️' },
    { key: 'economic', label: 'Economic', emoji: '💰' },
    { key: 'community', label: 'Community', emoji: '💬' },
  ];

  const filtered = React.useMemo(() => {
    if (category === 'all') return videos;
    return videos.filter((v) => v.category === category);
  }, [videos, category]);

  return (
    <section id="video-library" className="py-12 sm:py-16 md:py-20 bg-background">
      <div className="container px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-12">
          <Badge variant="outline" className="mb-4 text-sm font-semibold">
            🎬 Video Library
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3">
            See The Platform In Action
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Short demos of voting, economic tools, and community features.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8 sm:mb-10">
          {categories.map((cat) => (
            <Button
              key={cat.key}
              size="sm"
              variant={category === cat.key ? 'default' : 'outline'}
              onClick={() => {
                setCategory(cat.key);
                window.localStorage.setItem(storageKey, cat.key);
              }}
              className="text-xs sm:text-sm"
            >
              <span className="mr-1.5">{cat.emoji}</span>
              {cat.label}
            </Button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="max-w-3xl mx-auto text-center py-12 bg-muted/30 rounded-lg border-2 border-dashed border-muted">
            <p className="text-muted-foreground">No videos yet for this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {filtered.map((video) => (
              <Card
                key={video.id}
                className="overflow-hidden group cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02] animate-in fade-in slide-in-from-bottom-4 duration-500"
                onClick={() => handleOpen(video, true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen(video, true);
                  }
                }}
              >
                <div className="relative aspect-video bg-muted overflow-hidden">
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                  {/* Overlay badges container (top-right security, bottom-left features) */}
                  <div className="absolute inset-0 flex flex-col justify-between p-3 pointer-events-none">
                    <div className="flex justify-end">{renderLCBadges(video, 'top-right')}</div>
                    <div className="flex flex-wrap gap-2">
                      {renderLCBadges(video, 'bottom-left')}
                    </div>
                  </div>

                  {/* Center play button */}
                  <div className="absolute inset-0 flex items-center justify-center transition-all duration-300 opacity-80 scale-95">
                    <div className="bg-primary/90 backdrop-blur-sm rounded-full p-4 transform transition-transform duration-300 group-hover:scale-110 shadow-xl">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-8 w-8 text-primary-foreground"
                      >
                        <polygon points="6 3 20 12 6 21 6 3" />
                      </svg>
                    </div>
                  </div>

                  {/* Duration badge bottom-right */}
                  <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-all duration-200 border-transparent absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3 mr-1"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    {video.duration}
                  </div>

                  {/* Optional top-left small badge (e.g., encrypted) */}
                  <div className="absolute top-3 left-3">{renderLCBadges(video, 'top-left')}</div>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-lg" aria-hidden>
                      {video.icon ?? '🎥'}
                    </span>
                    <h3 className="text-lg sm:text-xl font-bold mb-1 leading-snug group-hover:text-primary transition-colors">
                      {video.title}
                    </h3>
                  </div>
                  <p className="text-sm sm:text-base text-muted-foreground line-clamp-2 leading-relaxed">
                    {video.description}
                  </p>
                  {/* Inline "Watch demo" prompt under text (slides in on hover) */}
                  <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-primary transition-all duration-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <polygon points="6 3 20 12 6 21 6 3"></polygon>
                    </svg>
                    <span>Watch demo</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        <VideoLibraryDialog
          open={open}
          setOpen={setOpen}
          video={active}
          cityName={cityName}
          onPrimaryCTA={onPrimaryCTA}
          autoPlayWithSound={autoPlayWithSound}
        />
      </div>
    </section>
  );
}

// Dialog for active video
export function VideoLibraryDialog({
  open,
  setOpen,
  video,
  cityName,
  onPrimaryCTA,
  autoPlayWithSound = true,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  video: VideoLibraryItem | null;
  cityName?: string;
  onPrimaryCTA?: () => void;
  autoPlayWithSound?: boolean;
}) {
  if (!video) return null;
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [videoSrc, setVideoSrc] = React.useState<string | undefined>(undefined);

  // Manage lazy-load, autoplay on open, and pause/reset on close
  React.useEffect(() => {
    const el = videoRef.current;
    if (open && video) {
      setVideoSrc(video.videoUrl);
      // Try autoplay when dialog opens
      // Many browsers require muted for autoplay to work reliably
      if (el) {
        el.muted = !autoPlayWithSound ? true : false;
        el.volume = autoPlayWithSound ? 1 : el.volume;
        el.autoplay = true as any;
        // Force a load before attempting to play; delay until visible
        const attemptPlay = () => {
          el.play().catch(() => {
            // As a fallback, keep controls for manual play
          });
        };
        // Load source explicitly when we set src via state
        el.load();
        const onCanPlay = () => {
          el.removeEventListener('canplay', onCanPlay);
          attemptPlay();
        };
        el.addEventListener('canplay', onCanPlay);
        // Also try on the next frame and next tick
        requestAnimationFrame(() => {
          setTimeout(attemptPlay, 0);
        });
      }
    } else {
      // Closing: pause and unload
      if (el) {
        try {
          el.pause();
          el.currentTime = 0;
        } catch {}
      }
      setVideoSrc(undefined);
    }
  }, [open, video, autoPlayWithSound]);

  const handlePrimary = () => {
    if (onPrimaryCTA) onPrimaryCTA();
    else {
      const el = document.getElementById('interest-form');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-4xl w-[95vw] sm:w-auto max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl sm:text-3xl flex items-center gap-3">
            <span className="text-3xl sm:text-4xl">{video.icon ?? '🎥'}</span>
            <span>{video.title}</span>
          </DialogTitle>
          <DialogDescription>Full demonstration of this feature</DialogDescription>
          <DialogClose asChild>
            <button
              aria-label="Close"
              className="absolute right-4 top-4 rounded-full p-2 hover:bg-accent"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogClose>
        </DialogHeader>
        <div className="aspect-video bg-black rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            src={videoSrc}
            poster={video.thumbnailUrl}
            controls
            className="w-full h-full"
            playsInline
            autoPlay
            // muted is managed dynamically to allow sound-by-default when permitted
          >
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="space-y-4 pt-2">
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            {video.description}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="capitalize" variant="outline">
              {(video.icon ?? '🎥') + ' '}
              {video.category}
            </Badge>
            {video.featured && (
              <Badge className="bg-primary text-primary-foreground border-transparent">
                Featured
              </Badge>
            )}
            <Badge variant="secondary">{video.duration}</Badge>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button className="h-11" onClick={handlePrimary}>
              I Want This for {cityName ?? 'My City'}
            </Button>
            <Button variant="outline" className="h-11" onClick={() => setOpen(false)}>
              Close Demo
            </Button>
          </div>
          <p className="text-xs text-muted-foreground pt-2">
            This feature can be customized for {cityName ?? 'your community'}.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function renderLCBadges(video: VideoLibraryItem, position: VideoBadge['position']) {
  const id = video.id;
  // Group Buying (economic-group-buying)
  if (id === 'economic-group-buying') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-green-600 hover:bg-green-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <TrendingDown className="h-3.5 w-3.5 mr-1" />
          Save 58%
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-white border-none text-xs px-2.5 py-1">
            <Calendar className="h-3 w-3 mr-1" />7 days left
          </Badge>
          <Badge className="bg-purple-600/90 hover:bg-purple-600/90 backdrop-blur-sm text-white border-none text-xs px-2.5 py-1">
            <Users className="h-3 w-3 mr-1" />
            32 joined
          </Badge>
          <Badge className="bg-orange-600/90 hover:bg-orange-600/90 backdrop-blur-sm text-white border-none text-xs px-2.5 py-1 font-semibold">
            -7 more needed
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100">
          <Car className="w-3 h-3 mr-1" />
          service
        </Badge>
      );
    }
  }
  // Voter Profile (governance-voter-profile)
  if (id === 'governance-voter-profile') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-purple-600 hover:bg-purple-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Shield className="h-3.5 w-3.5 mr-1" />
          Private Profile
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Vote className="h-3 w-3 mr-1" />
            Track Your Votes
          </Badge>
          <Badge className="bg-indigo-600/90 hover:bg-indigo-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <BarChart3 className="h-3 w-3 mr-1" />
            Compare w/ Council
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-indigo-100 text-indigo-800 border-none hover:bg-indigo-100">
          <Vote className="w-3 h-3 mr-1" />
          governance
        </Badge>
      );
    }
  }
  // City Council Profile (governance-city-council-profile)
  if (id === 'governance-city-council-profile') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Database className="h-3.5 w-3.5 mr-1" />
          Public Records
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Building2 className="h-3 w-3 mr-1" />
            Council Votes
          </Badge>
          <Badge className="bg-teal-600/90 hover:bg-teal-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <BarChart3 className="h-3 w-3 mr-1" />
            Alignment Score
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-emerald-100 text-emerald-800 border-none hover:bg-emerald-100">
          <Building2 className="w-3 h-3 mr-1" />
          public records
        </Badge>
      );
    }
  }
  // Department Contacts (community-department-contacts)
  if (id === 'community-department-contacts') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-cyan-600 hover:bg-cyan-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Mail className="h-3.5 w-3.5 mr-1" />
          Direct Access
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Network className="h-3 w-3 mr-1" />
            Org Chart
          </Badge>
          <Badge className="bg-violet-600/90 hover:bg-violet-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Mail className="h-3 w-3 mr-1" />
            Message Leaders
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-cyan-100 text-cyan-800 border-none hover:bg-cyan-100">
          <Network className="w-3 h-3 mr-1" />
          organization
        </Badge>
      );
    }
  }
  // Approved Vendors (economic-approved-vendors)
  if (id === 'economic-approved-vendors') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-green-600 hover:bg-green-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <CheckCircle className="h-3.5 w-3.5 mr-1" />
          Verified Vendors
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Users className="h-3 w-3 mr-1" />
            Get Bids
          </Badge>
          <Badge className="bg-amber-600/90 hover:bg-amber-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <DollarSign className="h-3 w-3 mr-1" />
            Compare Quotes
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-green-100 text-green-800 border-none hover:bg-green-100">
          <CheckCircle className="w-3 h-3 mr-1" />
          verified
        </Badge>
      );
    }
  }
  // Marketplace Listings (community-marketplace-listings)
  if (id === 'community-marketplace-listings') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-red-600 hover:bg-red-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Shield className="h-3.5 w-3.5 mr-1" />
          No Spammers
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <ShoppingBag className="h-3 w-3 mr-1" />
            Sell & Buy
          </Badge>
          <Badge className="bg-purple-600/90 hover:bg-purple-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <MessageSquare className="h-3 w-3 mr-1" />
            Safe Messaging
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-rose-100 text-rose-800 border-none hover:bg-rose-100">
          <ShoppingBag className="w-3 h-3 mr-1" />
          marketplace
        </Badge>
      );
    }
  }
  // Elections Voting (governance-elections-voting)
  if (id === 'governance-elections-voting') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Lock className="h-3.5 w-3.5 mr-1" />
          Anonymous Ballot
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-violet-600/90 hover:bg-violet-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Shield className="h-3 w-3 mr-1" />
            Verifiable Tally
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-indigo-100 text-indigo-800 border-none hover:bg-indigo-100">
          <Lock className="w-3 h-3 mr-1" />
          private
        </Badge>
      );
    }
  }
  // Infrastructure Proposals (governance-infrastructure-proposals)
  if (id === 'governance-infrastructure-proposals') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-sky-600 hover:bg-sky-600 text-gray-600 border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Landmark className="h-3.5 w-3.5 mr-1" />
          Escrow Protected
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-green-600/90 hover:bg-green-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <HandCoins className="h-3 w-3 mr-1" />
            Crowdfund $150K
          </Badge>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Users className="h-3 w-3 mr-1" />
            $15 per Resident
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-teal-100 text-teal-800 border-none hover:bg-teal-100">
          <Landmark className="w-3 h-3 mr-1" />
          infrastructure
        </Badge>
      );
    }
  }
  // Community Services Booking (community-services-booking)
  if (id === 'community-services-booking') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-green-600 hover:bg-green-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Calendar className="h-3.5 w-3.5 mr-1" />
          Instant Booking
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-white border-none text-xs px-2.5 py-1">
            <CreditCard className="h-3 w-3 mr-1" />
            Pay In-App
          </Badge>
          <Badge className="bg-violet-600/90 hover:bg-violet-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Clock className="h-3 w-3 mr-1" />
            Real-Time Slots
          </Badge>
        </>
      );
    }
    if (position === 'top-left') {
      return (
        <Badge className="bg-green-100 text-green-800 border-none hover:bg-green-100">
          <Calendar className="w-3 h-3 mr-1" />
          booking
        </Badge>
      );
    }
  }
  // Facebook Groups Migration (social-facebook-groups)
  if (id === 'social-facebook-groups') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-blue-600 hover:bg-blue-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Users className="h-3.5 w-3.5 mr-1" />
          Migration Ready
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-green-600/90 hover:bg-green-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Shield className="h-3 w-3 mr-1" />
            Authenticated Members
          </Badge>
          <Badge className="bg-indigo-600/90 hover:bg-indigo-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <BarChart3 className="h-3 w-3 mr-1" />
            App Tools
          </Badge>
        </>
      );
    }
  }
  // Community News (social-community-news)
  if (id === 'social-community-news') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-rose-600 hover:bg-rose-600 text-gray-600 border-none font-bold text-sm px-3 py-1 shadow-lg">
          <Calendar className="h-3.5 w-3.5 mr-1" />
          Events Feed
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Users className="h-3 w-3 mr-1" />
            RSVP & Attendees
          </Badge>
          <Badge className="bg-violet-600/90 hover:bg-violet-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <MessageSquare className="h-3 w-3 mr-1" />
            Discussions
          </Badge>
        </>
      );
    }
  }
  // Community Forum (social-community-forum)
  if (id === 'social-community-forum') {
    if (position === 'top-right') {
      return (
        <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white border-none font-bold text-sm px-3 py-1 shadow-lg">
          <MessageSquare className="h-3.5 w-3.5 mr-1" />
          Ongoing Threads
        </Badge>
      );
    }
    if (position === 'bottom-left') {
      return (
        <>
          <Badge className="bg-green-600/90 hover:bg-green-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <Calendar className="h-3 w-3 mr-1" />
            Schedule Meeting
          </Badge>
          <Badge className="bg-blue-600/90 hover:bg-blue-600/90 backdrop-blur-sm text-gray-600 border-none text-xs px-2.5 py-1">
            <ThumbsUp className="h-3 w-3 mr-1" />
            Like/Dislike
          </Badge>
        </>
      );
    }
  }
  // Fallbacks: top-left featured badge if nothing else
  if (position === 'top-left' && video.featured) {
    return (
      <Badge variant="default" className="bg-primary/90 backdrop-blur-sm">
        Featured
      </Badge>
    );
  }
  return null;
}
